import * as React from 'react';
import {
    TreeList,
    orderBy,
    filterBy,
    mapTree,
    extendDataItem,
    TreeListSelectionCell,
    TreeListHeaderSelectionChangeEvent,
    TreeListSelectionChangeEvent,
    TreeListExpandChangeEvent,
    TreeListDataStateChangeEvent,
    TreeListTextFilter} from '@progress/kendo-react-treelist';
import { getter } from '@progress/kendo-react-common';
import { SortDescriptor, FilterDescriptor } from '@progress/kendo-data-query';
import { SvgIcon } from '@progress/kendo-react-common';
import { circleIcon, chevronDownIcon, chevronRightIcon } from '@progress/kendo-svg-icons';
import { GetBulkFlowIcon, GetEndpointIcon, GetEndpointMP2Icon, GetGatewayIcon, GetGatewayHMEIcon, GetHeadUnitIcon } from '../../../../../Utilities/CustomIcons';
import { DeviceType } from '../../../../../Enums/DeviceType';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDiagramProject, faQuestion } from '@fortawesome/free-solid-svg-icons';
import { enMessages } from '../../../../../messages/en-US';
import { useLocalization } from '@progress/kendo-react-intl';


const DATA_ITEM_KEY: string = 'id';
const SUB_ITEMS_FIELD: string = 'endpoints';
const EXPAND_FIELD: string = 'expanded';
const SELECTED_FIELD: string = 'selected';
const idGetter: (values: any) => any = getter(DATA_ITEM_KEY);

const extendData = (dataState: any[], selectedState: { [id: string]: number[] | boolean }, expandedState: { [n: number]: boolean }) => {
    return mapTree(dataState, SUB_ITEMS_FIELD, (item) =>
        extendDataItem(item, SUB_ITEMS_FIELD, {
            selected: selectedState[idGetter(item)],
            expanded: expandedState[idGetter(item)]
        })
    );
};

const headerSelectionValue = (dataState, selectedState) => {
    let allSelected = true;
    mapTree(dataState, SUB_ITEMS_FIELD, (item) => {
        allSelected = allSelected && selectedState[idGetter(item)];
        return item;
    });
    return allSelected;
};

interface AppState {
    data: any[];
    dataState: {
        sort: SortDescriptor[];
        filter: FilterDescriptor[];
    };
    expanded: number[];
    selectedState: { [id: string]: boolean };
}

interface Props {
    setSelectedItems: (items: any[]) => void;
    asset: any;
    selectionState: { [id: string]: boolean };
    setSelectionState: (state: { [id: string]: boolean }) => void;
}

const transformAssetData = (asset: any) => {
    // Group endpoints by their groupId
    const endpointsByGroup = asset.assetConfigurationDetails.endpoints.reduce((acc: any, endpoint: any) => {
        if (!acc[endpoint.groupId]) {
            acc[endpoint.groupId] = [];
        }
        acc[endpoint.groupId].push({
            id: endpoint.id,
            name: endpoint.name,
            complianceStatus: endpoint.compliance.status, // Endpoint level: compliance.status
            connectivityStatus: endpoint.connectivity, // Endpoint level: connectivity (string)
            lastActivity: endpoint.lastActivity,
            entityType: endpoint.entityType
        });
        return acc;
    }, {});

    // Create the final data structure
    return asset.assetConfigurationDetails.groups.map((group: any) => ({
        id: group.id,
        name: group.name,
        complianceStatus: group.complianceStatus, // Group level: complianceStatus (string)
        connectivityStatus: group.connectivityStatus, // Group level: connectivityStatus (string)
        endpoints: endpointsByGroup[group.id] || [],
        entityType: group.entityType
    }));
};

// Function to get the worst status color between compliance and connectivity
const getWorstStatusColor = (compliance: string, connectivity: string) => {
    const getStatusPriority = (status: string) => {
        switch (status) {
            case "NonCompliant":
            case "Offline":
                return 3;
            case "PartiallyCompliant":
            case "Partial":
                return 2;
            case "Compliant":
            case "Online":
                return 1;
            default:
                return 0; // Unknown or other statuses
        }
    };

    const compliancePriority = getStatusPriority(compliance);
    const connectivityPriority = getStatusPriority(connectivity);
    const worstPriority = Math.max(compliancePriority, connectivityPriority);

    // Return color based on the worst priority
    switch (worstPriority) {
        case 3: // NonCompliant or Offline
            return '#FF3630';
        case 2: // PartiallyCompliant or Partial
            return '#FFBF00';
        case 1: // Compliant or Online
            return '#3FA43F';
        default: // Unknown or other
            return '#85868E';
    }
};

const NameCell = (props: any) => {
    // Indentation and expand/collapse logic
    const { expandable, hasChildren, expanded, level, onExpandChange, dataItem } = props;
    const icons: React.ReactNode[] = [];

    // Indentation
    if (expandable && level) {
        for (let i = 1; i < level.length; i++) {
            icons.push(<span style={{ display: 'inline-block', width: 16 }} key={i} />);
        }
        // Expand/collapse icon
        if (hasChildren) {
            icons.push(
                <span
                    style={{ cursor: 'pointer', display: 'inline-block', width: 16, marginRight: '8px'}}
                    key="expand-collapse"
                    onClick={e => onExpandChange && onExpandChange(e, dataItem, level)}
                >
                    {expanded ? <SvgIcon icon={chevronDownIcon} size="large" /> : <SvgIcon icon={chevronRightIcon} size="large" />}
                </span>
            );
        } else {
            icons.push(<span style={{ display: 'inline-block', width: 16}} key="empty" />);
        }
    }
    
    // Get worst case status color between compliance and connectivity
    const complianceStatus = dataItem.complianceStatus || 'Unknown';
    const connectivityStatus = dataItem.connectivityStatus || 'Unknown';
    const statusColor = getWorstStatusColor(complianceStatus, connectivityStatus);
    
    const statusIcon = (
        <SvgIcon icon={circleIcon} style={{ color: statusColor }} size="large" />
    );

    const getTypeIcon = () => {
        switch (dataItem.entityType) {
            case 'EndpointMP2':
                return GetEndpointMP2Icon(18, 18);
            case 'EndpointLFHF':
            case 'EndpointHF':
            case 'EndpointLF':
            case 'Endpoint':
                return GetEndpointIcon(20, 20);
            case 'EndpointBF':
                return GetBulkFlowIcon(20, 20);
            case 'Group':
                return <FontAwesomeIcon icon={faDiagramProject} style={{width: '18px', height: '18px'}} />;
            default:
                return <FontAwesomeIcon icon={faQuestion} size="sm" />;
        }
    };

    const typeIcon = (
        <div style={{display: 'inline-block', width: '16px', height: '16px'}}> 
            {getTypeIcon()}
        </div>
    );


    return (
        <td style={{ 
            whiteSpace: 'nowrap', 
            overflow: 'hidden', 
            textOverflow: 'ellipsis',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '2px'
        }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                {icons}
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                {typeIcon}
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                {statusIcon}
            </div>
            <div style={{ 
                display: 'flex', 
                alignItems: 'center',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
            }}>
                {dataItem.name}
            </div>
            
        </td>
    );
};

const EmptyHeaderCell = () => null;

const TreeComponent: React.FC<Props> = ({ setSelectedItems, asset, selectionState, setSelectionState }) => {
    const localizationService = useLocalization();
    const [state, setState] = React.useState<AppState>({
        data: transformAssetData(asset),
        dataState: {
            sort: [{ field: 'name', dir: 'asc' as 'asc' | 'desc' }],
            filter: []
        },
        expanded: asset?.assetConfigurationDetails?.groups?.map((g: any) => g.id) || [],
        selectedState: selectionState // Initialize with the lifted state
    });

    // Update local state when lifted state changes
    React.useEffect(() => {
        setState(prev => ({
            ...prev,
            selectedState: selectionState
        }));
    }, [selectionState]);

    // Update data when asset changes
    React.useEffect(() => {
        if (asset && asset.assetConfigurationDetails) {
            setState(prev => ({
                ...prev,
                data: transformAssetData(asset),
                expanded: asset.assetConfigurationDetails.groups?.map((g: any) => g.id) || []
            }));
        }
    }, [asset]);

    const onExpandChange = React.useCallback(
        (e: TreeListExpandChangeEvent) => {
            setState((prevState) => ({
                ...prevState,
                expanded: e.value ? prevState.expanded.filter((id) => id !== e.dataItem.id) : [...prevState.expanded, e.dataItem.id]
            }));
        },
        []
    );

    const handleDataStateChange = (event: TreeListDataStateChangeEvent) => {
        setState((prevState) => ({
            ...prevState,
            dataState: {
                ...prevState.dataState,
                ...event.dataState
            }
        }));
    };

    const onSelectionChange = React.useCallback(
        (event: TreeListSelectionChangeEvent) => {
            const newSelectedState: { [id: string]: boolean } = { ...state.selectedState };
            const itemId = idGetter(event.dataItem);
            const isParent = !!event.dataItem.endpoints;

            // Toggle selection for the clicked item
            const selected = !newSelectedState[itemId];
            newSelectedState[itemId] = selected;

            // If parent, select/deselect all children
            if (isParent) {
                event.dataItem.endpoints.forEach((child: any) => {
                    newSelectedState[child.id] = selected;
                });
            } else {
                // If child, update parent selection
                const parentId = findParentId(state.data, itemId);
                if (parentId !== null) {
                    const parent = state.data.find((g: any) => g.id === parentId);
                    newSelectedState[parentId] = areAllChildrenSelected(parent, newSelectedState);
                }
            }

            // Update both local and lifted state
            setState((prevState) => ({ ...prevState, selectedState: newSelectedState }));
            setSelectionState(newSelectedState);

            // Update the selected items, filtering to include only endpoint IDs
            const selectedItems = Object.keys(newSelectedState)
                .filter(key => newSelectedState[key])
                .filter(id => {
                    // Check if the id belongs to an endpoint
                    return state.data.some(group => 
                        group.endpoints.some(endpoint => endpoint.id.toString() === id)
                    );
                });

            setSelectedItems([...selectedItems]);
        },
        [state.selectedState, setSelectedItems, state.data, setSelectionState]
    );

    const onHeaderSelectionChange = React.useCallback((event: TreeListHeaderSelectionChangeEvent) => {
        const checkboxElement: any = event.syntheticEvent.target;
        const checked: boolean = checkboxElement.checked;
        const newSelectedState = {};

        event.dataItems.forEach((item) => {
            newSelectedState[idGetter(item)] = checked;
        });
        
        // Update both local and lifted state
        setState((prevState) => ({ ...prevState, selectedState: newSelectedState }));
        setSelectionState(newSelectedState);
    }, [setSelectionState]);

    const convertExpandedArrayToObject = (expandedArray: number[]): { [n: number]: boolean } => {
        return expandedArray.reduce((acc, id) => {
            acc[id] = true;
            return acc;
        }, {} as { [n: number]: boolean });
    };

    const processData = () => {
        let { data, dataState } = state;
        let filteredData: any[] = filterBy(data, dataState.filter, SUB_ITEMS_FIELD);
        let sortedData: any[] = orderBy(filteredData, dataState.sort, SUB_ITEMS_FIELD);
        const expandedState = convertExpandedArrayToObject(state.expanded);
        return extendData(sortedData, state.selectedState, expandedState);
    };

    const columns = [
        {
            field: 'selected',
            width: '7%',
            headerSelectionValue: headerSelectionValue(state.data, state.selectedState),
            cell: TreeListSelectionCell,
            headerCell: EmptyHeaderCell
        },
        { 
            field: 'name', 
            title: localizationService.toLanguageString('custom.name', enMessages.custom.name), 
            width: '250px', 
            filter: TreeListTextFilter, 
            expandable: true,
            cell: NameCell
        },
    ];

    // Helper to find parent by child id
    const findParentId = (data: any[], childId: any): any => {
        for (const item of data) {
            if (item.endpoints && item.endpoints.some((ep: any) => ep.id === childId)) {
                return item.id;
            }
        }
        return null;
    };

    // Helper to check if all children are selected
    const areAllChildrenSelected = (parent: any, selectedState: { [id: string]: boolean }) => {
        if (!parent.endpoints) return false;
        return parent.endpoints.every((child: any) => selectedState[child.id]);
    };

    return (
        <TreeList
            style={{overflow: 'auto', height: '100%', border: 'none',}}
            data={processData()}
            selectedField={SELECTED_FIELD}
            expandField={EXPAND_FIELD}
            subItemsField={SUB_ITEMS_FIELD}
            dataItemKey={DATA_ITEM_KEY}
            onSelectionChange={onSelectionChange}
            onHeaderSelectionChange={onHeaderSelectionChange}
            onExpandChange={onExpandChange}
            sortable={{ mode: 'multiple' }}
            {...state.dataState}
            onDataStateChange={handleDataStateChange}
            columns={columns}
            className='tree-list-scrollable-background checkbox-outline-override'
        />
    );
};

export default TreeComponent;